import { createFileRoute, Link } from "@tanstack/react-router";
import { ServicePageLayout } from "@/components/service/ServicePageLayout";
import { serviceHead } from "@/lib/service-head";
import { loadPageSettings } from "@/lib/page-seo-loader";
import type { Faq } from "@/lib/service-schema";

const SLUG = "/emergency-roof-leak-repair";
const NAME = "Emergency Roof Leak Repair";
const TITLE = "Emergency Roof Leak Repair in Phoenix | Durafoam Roofing";
const DESCRIPTION =
  "Fast emergency roof leak response across the Phoenix Valley — tarp service, leak diagnosis, and permanent repair. Call 480-941-5373.";

const faqs: Faq[] = [
  { q: "Are you available 24/7 for emergencies?", a: "Yes. Call or text 480-941-5373 anytime — we monitor the line 24/7 and will get back to you as soon as possible, even outside business hours." },
  { q: "How fast can you respond to an emergency leak?", a: "We aim for same-day or next-day response during active leak events, especially during monsoon season. Call 480-941-5373 directly — we triage emergency calls first." },
  { q: "Can you tarp my roof?", a: "Yes. If we can't do a permanent repair the same day because of weather or scope, we can deploy a tarp to buy you time and prevent further interior damage." },
  { q: "What should I do before you arrive?", a: "Get buckets under active drips, move valuables out of affected rooms, and shut off power to any fixture water is contacting. Don't go on the roof — most homeowner falls happen during emergency leak response." },
  { q: "Will insurance cover my emergency repair?", a: "Storm-driven damage from monsoon or hail is often covered. Wear-out usually isn't. We document conditions thoroughly so the claim has what it needs." },
  {
    q: "Is emergency service more expensive?",
    a: "We don't gouge on emergencies. Pricing reflects after-hours or weekend labor when applicable, but the work itself is priced the same way we'd price a scheduled repair. See our roofing costs guide for typical repair pricing.",
    aNode: (
      <>
        We don't gouge on emergencies. Pricing reflects after-hours or weekend labor when
        applicable, but the work itself is priced the same way we'd price a scheduled repair. See
        our{" "}
        <Link to="/roof-repair-cost-phoenix" className="font-semibold text-primary underline">
          roofing costs guide
        </Link>{" "}
        for typical repair pricing.
      </>
    ),
  },
];

export const Route = createFileRoute("/emergency-roof-leak-repair")({
  loader: () => loadPageSettings(SLUG),
  head: ({ loaderData }) => serviceHead({ override: loaderData?.seo, slug: SLUG, title: TITLE, description: DESCRIPTION, serviceName: NAME, faqs, rating: loaderData?.rating, reviewCount: loaderData?.reviewCount }),
  component: Page,
});

function Page() {
  return (
    <ServicePageLayout
      pageSlug="emergency-roof-leak-repair"
      eyebrow="Emergency Service"
      h1="Emergency Roof Leak Repair in Phoenix"
      subhead="Active leak? Storm damage? Call or text 480-941-5373 anytime — we monitor 24/7 and triage emergency calls first, with same-day tarp service when conditions or scope require it."
      heroBullets={["Same-day response targeted", "Tarp service available", "Insurance documentation", "Permanent repair after triage"]}
      whatIs={{
        heading: "What counts as a roofing emergency",
        body: (
          <>
            <p>
              A roofing emergency is any situation where active water entry is causing or
              threatening interior damage. Monsoon-driven leaks, wind-lifted shingles, tree
              branches through the roof, and post-storm exposed decking all qualify. So does a
              steady drip that's saturating drywall — slow leaks ruin ceilings just as
              effectively as fast ones.
            </p>
            <p>
              Our emergency service has two phases: triage and permanent repair. Triage stops the
              water — usually with a tarp, a temporary patch, or a quick flashing reseal. The
              permanent repair follows once we can safely access the area, often within a few
              days. Both phases are documented for your records and for any insurance claim.
            </p>
            <p>
              Call 480-941-5373 for emergencies. We don't bury the phone number behind a contact
              form — you should be able to reach a person fast when water is coming in.
            </p>
          </>
        ),
      }}
      climate={{
        heading: "Phoenix monsoon and emergency leaks",
        body: (
          <>
            <p>
              Most of our emergency calls cluster in the July–September monsoon window. Phoenix's
              monsoon storms are short but violent — an inch of rain in twenty minutes, with
              60–80 mph gusts that lift shingles and drive water under tile. Roofs that hold fine
              against normal rain often fail spectacularly under a monsoon cell.
            </p>
            <ul>
              <li><strong>Wind-lifted shingles:</strong> Shingles that have lost their sealant bond peel up in microbursts. Once a shingle lifts, the underlayment is exposed.</li>
              <li><strong>Slid or fractured tile:</strong> Wind and debris dislodge or crack tiles, exposing the underlayment to direct rain.</li>
              <li><strong>Failed pipe boots:</strong> A pipe boot that was 90% gone before the storm becomes a fire hose during monsoon.</li>
              <li><strong>Backed-up drains:</strong> Palm fronds and gravel clog drains and scuppers; flat roofs flood and find every weakness.</li>
              <li><strong>Tree damage:</strong> Mesquite and palo verde branches snap and puncture roofs in high wind events.</li>
            </ul>
          </>
        ),
      }}
      process={{
        heading: "Our emergency response process",
        steps: [
          { title: "Call triage", body: "Call or text 480-941-5373. We monitor around the clock — we assess the situation, prioritize the response, and dispatch the nearest available crew." },
          { title: "Stop the water", body: "Tarp, temporary patch, or quick flashing reseal — whatever stops the leak fastest while staying safe on the roof." },
          { title: "Document for insurance", body: "Photos, conditions, and a written incident report — useful if you're filing a claim." },
          { title: "Permanent repair", body: "Once weather and scope allow, we return for the permanent fix with appropriate materials and full workmanship warranty." },
        ],
      }}
      testimonial={{
        quote:
          "Brad and his team were amazing! They responded immediately to my request and got me scheduled right away. They came as promised, and they did an amazing job for a very reasonable price. I was VERY impressed by the professionalism. Thank you Brad and team!",
        name: "Suzanne Juneau",
        source: "Google review",
        context: "Roof repair for storm & wind damage",
      }}
      faqs={faqs}
      ctaLabel="Request Emergency Service"
      ctaTitle="Roof leaking right now?"
      ctaBody="Don't wait. Call 480-941-5373 — we'll triage immediately and get a crew dispatched."
    />
  );
}
